import { useRouter } from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import { SearchForm } from 'components/common';

import type { ButtonSize } from './Button';

type Props = {
  initialValue?: string;
  placeholder?: string;
  size?: ButtonSize;
} & (
  | {
      onSearch: ({
        page,
        search,
        throwError
      }: {
        page?: number;
        search?: string;
        throwError?: boolean;
      }) => Promise<void> | void;
      searchKey?: never;
      withUrl?: false;
    }
  | { onSearch?: never; searchKey?: string; withUrl: true }
);

const Search = ({
  initialValue,
  onSearch,
  placeholder,
  size,
  searchKey = 'search',
  withUrl = false
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const router = useRouter();

  const handleClear = async () => {
    if (withUrl) handleSearchWithUrl('');
    else await onSearch({ page: 1, search: '' });
  };

  const handleError = async (err: Error) => dispatch.handleErrorDispatch(err);

  const handleSearch = async (value: string) => {
    await onSearch({ page: 1, search: value, throwError: true });
  };

  const handleSearchWithUrl = (value: string) => {
    const query = { ...router.query };
    if (query.page) query.page = '1';

    if (value) query[searchKey] = value;
    else delete query[searchKey];

    router.replace({ query }, undefined, { shallow: true });
  };

  return (
    <SearchForm
      initialValue={initialValue}
      placeholderText={placeholder || messages.commonSearch}
      onClear={handleClear}
      onError={handleError}
      size={size || 'header'}
      {...(withUrl
        ? {
            initialValue: router.query[searchKey] as string,
            onSearch: handleSearchWithUrl
          }
        : { onSearch: handleSearch })}
    />
  );
};

export default Search;
